import { GenData, QMulti, ArchiveProvider } from '@educraft/base';

export interface ArchiveGenMultiData {
  subjectID: string;
  categoryID: string;
  topicID: string;
  gradeID: string;
  Task: string;
  Questions: {
    Texts: string[];
    Answers: string[];
    AnswerIndex: number;
  }[];
}

const archiveConfig = new Map<string, { archiveName: string; filePath: string }>();
const dataCache = new Map<string, ArchiveGenMultiData>();

export function configureArchiveGenMulti(id: string, archiveName: string, filePath: string): void {
  archiveConfig.set(id, { archiveName, filePath });
}

export class ArchiveGenMulti {
  name = 'ArchiveGenMulti';
  private configId = '';

  constructor(configId = '') {
    this.configId = configId;
    this.name = `ArchiveGenMulti_${configId}`;
  }

  getRequiredArchiveUrls(): string[] {
    const params = this.getArchiveParams();
    return params ? [params.archiveName] : [];
  }

  private getArchiveParams(): { archiveName: string; filePath: string } | null {
    if (this.configId) {
      const config = archiveConfig.get(this.configId);
      if (config) return config;
    }
    return null;
  }

  private getData(
    archive: ArchiveProvider,
    archiveName: string,
    filePath: string
  ): ArchiveGenMultiData | null {
    const cacheKey = `${archiveName}:${filePath}`;

    if (dataCache.has(cacheKey)) {
      return dataCache.get(cacheKey)!;
    }

    try {
      const content = archive.getText(archiveName, filePath);
      if (!content) {
        console.warn(`ArchiveGenMulti: File not found in archive: ${archiveName}/${filePath}`);
        return null;
      }
      const data = JSON.parse(content) as ArchiveGenMultiData;
      dataCache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`ArchiveGenMulti: Error loading data: ${error}`);
      return null;
    }
  }

  generate(genData: GenData): QMulti {
    const params = this.getArchiveParams();

    if (!params) {
      return {
        texts: ['ArchiveGenMulti not configured'],
        answers: ['A', 'B', 'C'],
        answerIndex: 0,
      };
    }

    const data = this.getData(genData.archive, params.archiveName, params.filePath);

    if (!data?.Questions || data.Questions.length === 0) {
      return {
        texts: ['No data available'],
        answers: ['A', 'B', 'C'],
        answerIndex: 0,
      };
    }

    const {random} = genData;
    const questionIndex = random.next(data.Questions.length);
    const question = data.Questions[questionIndex];

    const shuffledAnswers = [...question.Answers];
    for (let i = shuffledAnswers.length - 1; i > 0; i--) {
      const j = random.next(i + 1);
      [shuffledAnswers[i], shuffledAnswers[j]] = [shuffledAnswers[j], shuffledAnswers[i]];
    }

    const correctAnswer = question.Answers[question.AnswerIndex];
    const newAnswerIndex = shuffledAnswers.indexOf(correctAnswer);

    return {
      texts: question.Texts,
      answers: shuffledAnswers,
      answerIndex: newAnswerIndex,
    };
  }
}
